import { PocketRow, SelectField } from "@/components/ui";
import {
  useVoiceInputDevices,
  type VoiceInputChoice,
} from "@/desktop/use-voice-input-devices";
import { useT } from "@/i18n";
import { settingsVoiceMessages } from "@/i18n/messages/settings-voice";

/** The option that stands for "no device picked"; never a real device id. */
const AUTOMATIC = "automatic";

/**
 * Preferences › Voice: the microphone (desktop). Automatic or a named input;
 * the second line names the device in use whenever the value does not.
 */
export function VoiceMicrophoneRow({
  onChoose,
}: {
  onChoose?: (choice: VoiceInputChoice) => void;
}) {
  const t = useT(settingsVoiceMessages);
  const {
    available,
    devices,
    automaticDevice,
    selectedId,
    selectedName,
    selectedConnected,
    choose,
  } = useVoiceInputDevices({ onChoose });
  if (!available) return null;
  const missing =
    selectedId !== null && selectedName !== null && !selectedConnected
      ? { value: selectedId, label: t("notConnected", { name: selectedName }) }
      : null;
  const line =
    devices.length === 0
      ? t("noMicrophone")
      : !automaticDevice
        ? null
        : selectedId === null
          ? t("usingDevice", { name: automaticDevice.name })
          : missing
            ? t("usingDeviceForNow", { name: automaticDevice.name })
            : null;
  return (
    <PocketRow
      testId="settings-row-voice-microphone"
      title={t("microphone")}
      secondLine={line}
      trailing={
        <SelectField
          variant="value"
          value={selectedId ?? AUTOMATIC}
          options={[
            { value: AUTOMATIC, label: t("automatic") },
            ...devices.map((device) => ({
              value: device.id,
              label: device.name,
            })),
            ...(missing ? [missing] : []),
          ]}
          onChange={(value) => choose(value === AUTOMATIC ? null : value)}
          testId="voice-microphone"
          ariaLabel={t("microphone")}
        />
      }
    />
  );
}
